'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { allowanceLabel, fetchRevealAllowance, type RevealAllowance } from '@/lib/reveals';

interface MySubscription {
  planId: string;
  status: string;
  interval?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
}

/**
 * A member's own plan: what they are on, how many contact reveals they have left,
 * and the auto-renewal switch.
 *
 * There is deliberately no "cancel now" button. The client's rule is that a paid
 * month or year is not refundable and cannot be cancelled part-way through, so
 * the only control is renewal: switch it off and the plan runs to the end of the
 * period already paid for, then reverts to Free.
 */
export default function SubscriptionPanel() {
  const { user, profile, loading: authLoading } = useAuth();
  const supabase = useMemo(() => createClient(), []);

  const [sub, setSub] = useState<MySubscription | null>(null);
  const [allowance, setAllowance] = useState<RevealAllowance | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const [{ data: subData }, allow] = await Promise.all([
        supabase.rpc('my_subscription'),
        fetchRevealAllowance(supabase),
      ]);
      setSub((subData as MySubscription) ?? null);
      setAllowance(allow);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your membership.');
    } finally {
      setLoading(false);
    }
  }, [supabase, user]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const setAutoRenew = async (autoRenew: boolean) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch('/api/auto-renew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoRenew }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Could not change auto-renewal.');
      setSub((s) => (s ? { ...s, cancelAtPeriodEnd: !data.autoRenew } : s));
      setNotice(
        autoRenew
          ? 'Auto-renewal is back on — your plan will continue as normal.'
          : data.accessUntil
            ? `Auto-renewal is off. You keep everything until ${new Date(data.accessUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}, then your account returns to Free.`
            : 'Auto-renewal is off. You keep everything until the end of the period you have paid for.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change auto-renewal.');
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || loading) {
    return <div className="card h-40 animate-pulse bg-muted/40" />;
  }
  if (!user) return null;

  const tier = profile?.membership_tier ?? 'free';
  const isPaid = tier !== 'free' && sub?.status && ['active', 'trialing', 'past_due'].includes(sub.status);
  const autoRenewOn = !(sub?.cancelAtPeriodEnd ?? false);
  const renewsOn = sub?.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-1">
            Your membership
          </h2>
          <p className="text-2xl font-extrabold text-foreground capitalize">
            {tier}
            {sub?.status === 'trialing' && (
              <span className="ml-2 text-xs font-bold uppercase bg-amber-100 text-amber-800 rounded px-2 py-0.5 align-middle">
                Trial
              </span>
            )}
            {sub?.status === 'past_due' && (
              <span className="ml-2 text-xs font-bold uppercase bg-red-100 text-red-700 rounded px-2 py-0.5 align-middle">
                Payment failed
              </span>
            )}
          </p>
          {allowance && (
            <p className="text-sm text-muted-foreground mt-1">{allowanceLabel(allowance)}</p>
          )}
          {isPaid && renewsOn && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {autoRenewOn
                ? `Renews ${renewsOn}${sub?.interval ? ` (${sub.interval}ly)` : ''}`
                : `Access until ${renewsOn}, then returns to Free`}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          {!isPaid && (
            <Link href="/membership-pricing" className="btn-primary text-sm justify-center">
              <Icon name="ArrowUpCircleIcon" size={16} />
              See plans
            </Link>
          )}
          {isPaid && (
            <button
              type="button"
              onClick={() => setAutoRenew(!autoRenewOn)}
              disabled={busy}
              className={`text-sm justify-center whitespace-nowrap disabled:opacity-60 ${
                autoRenewOn ? 'btn-secondary' : 'btn-primary'
              }`}
            >
              {busy
                ? 'Saving…'
                : autoRenewOn
                  ? 'Turn auto-renewal off'
                  : 'Turn auto-renewal back on'}
            </button>
          )}
        </div>
      </div>

      {isPaid && (
        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed border-t border-border pt-3">
          Your {sub?.interval === 'year' ? 'year' : 'month'} is paid in full and is not refundable
          part-way through. Turning auto-renewal off keeps everything you have paid for until{' '}
          {renewsOn ?? 'the end of the period'}, after which your account returns to Free — nothing
          is lost, and you can turn renewal back on any time before then.
        </p>
      )}

      {notice && (
        <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-3">
          {notice}
        </p>
      )}
      {error && (
        <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
